import React, { useEffect } from 'react';

import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Chip from 'shared/atoms/Chip';
import Pill from 'shared/atoms/Pill';
import T from 'shared/atoms/Typography';

import theme from 'shared/themes/default';
import { courseStatus } from 'shared/const';

import AllCoursesOverviewTable from './AllCoursesOverviewTable';
import CourseWatchDataTable from './CourseWatchDataTable';
import EpisodesSeenGraph from './EpisodesSeenGraph';
import TableWithSearchAndExport from './TableWithSearchAndExport';

import { useWatchData } from '../Context';
import {
  listAggregatedWatchData,
  listCourseWatchData,
} from '../Context/graphql/queries';

const Wrapper = styled.div`
  max-width: 100%;
`;

const WrapperPill = styled.div`
  display: flex;
  margin-top: 108px;
  ${theme.breakpoints.mobile} {
    margin-top: 64px;
  }
`;

const TitleWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.m1};
  margin-bottom: ${theme.spacing.m3};
  margin-top: ${theme.spacing.m2};
`;

const ShowCourseData = () => {
  const { t } = useTranslation();
  const {
    isBackOffice,
    id,
    organizationSelectedId,
    fetchCoursesWatchData,
    allCoursesStats,
    loadingCoursesWatchData,
    courseSelected,
    onSelectItem,
    restClient,
    setCourseSelected,
    setWatchDataError,
    useLazyGraphQLQuery,
  } = useWatchData();
  const [listAggregatedWatchDataQuery, listAggregatedWatchDataResponse] =
    useLazyGraphQLQuery(listAggregatedWatchData);
  const [listCourseWatchDataQuery, listCourseWatchDataResponse] =
    useLazyGraphQLQuery(listCourseWatchData);

  const aggregatedWatchData =
    listAggregatedWatchDataResponse.data?.listAggregatedWatchData || [];
  const courseWatchData =
    listCourseWatchDataResponse.data?.listCourseWatchData || [];

  useEffect(() => {
    const { error } = listAggregatedWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listAggregatedWatchDataResponse]);

  useEffect(() => {
    const { error } = listCourseWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listCourseWatchDataResponse]);

  useEffect(() => {
    const filter = {};
    const variables = {};

    if (organizationSelectedId) {
      filter.organizationId = organizationSelectedId;
      variables.organizationId = organizationSelectedId;
    }

    if (courseSelected) {
      filter.courseId = courseSelected.courseId;
      variables.courseId = courseSelected.courseId;

      listCourseWatchDataQuery({ variables });
    } else {
      fetchCoursesWatchData({ variables });
    }

    listAggregatedWatchDataQuery({
      variables: {
        filter,
      },
    });
  }, [courseSelected, organizationSelectedId]);

  useEffect(() => {
    if (id && !loadingCoursesWatchData && allCoursesStats) {
      const course = allCoursesStats.find((item) => item.courseId === id);
      if (course) {
        setCourseSelected(course);
      } else {
        onSelectItem();
      }
    }
  }, [allCoursesStats, id, loadingCoursesWatchData]);

  let courseTitle = '';

  if (courseSelected) {
    courseTitle = courseSelected.name;
  } else {
    courseTitle =
      isBackOffice && organizationSelectedId
        ? t('watchData.titleAllCoursesOrganization')
        : t('watchData.allCourses');
  }

  return (
    <Wrapper>
      <WrapperPill>
        <Pill
          color={theme.palette.lightPure}
          fontColor={theme.palette.mainDark}
        >
          {t('watchData.course').toUpperCase()}
        </Pill>
      </WrapperPill>
      <TitleWrapper>
        <T
          color={theme.palette.brand}
          fontWeight="medium"
          phoneVariant="headingXs"
          variant="headingS"
        >
          {courseTitle}
        </T>
        {courseSelected?.status === courseStatus.ARCHIVED && (
          <Chip
            color={theme.palette.errorLight}
            label={t('watchData.deleted')}
            theme="dark"
          />
        )}
      </TitleWrapper>
      <EpisodesSeenGraph
        data={aggregatedWatchData}
        loading={listAggregatedWatchDataResponse.loading}
      />
      {!courseSelected && (
        <TableWithSearchAndExport
          itemsToDisplay={allCoursesStats || []}
          downloadItems={() =>
            restClient.get(
              organizationSelectedId
                ? `/watchData/organizations/${organizationSelectedId}/courses`
                : '/watchData/courses'
            )
          }
          tableTitle={t('watchData.activitiesOfYourCourses')}
          searchPlaceHolder={t('watchData.searchForCourses')}
          TableToDisplay={AllCoursesOverviewTable}
          showRowsAreClickableMessage
          tableProps={{
            selectCourseHandler: onSelectItem,
            showCatalogs: !organizationSelectedId,
          }}
          loading={loadingCoursesWatchData}
          spinnerDuration={organizationSelectedId ? 45 : 10}
          emptyMessage={t('watchData.noActivity')}
        />
      )}
      {courseSelected && (
        <TableWithSearchAndExport
          itemsToDisplay={courseWatchData}
          downloadItems={() =>
            restClient.get(
              organizationSelectedId
                ? `/watchData/organizations/${organizationSelectedId}/courses/${courseSelected.id}`
                : `/watchData/courses/${courseSelected.id}`
            )
          }
          tableTitle={t('watchData.activityOfYourUsers')}
          searchPlaceHolder={t('watchData.searchForUsers')}
          TableToDisplay={CourseWatchDataTable}
          loading={listCourseWatchDataResponse.loading}
          spinnerDuration={15}
          emptyMessage={t('watchData.noCourseActivity')}
        />
      )}
    </Wrapper>
  );
};

export default ShowCourseData;
